// Admin token deploy — client-side via the connected MetaMask.
// Backend serves the artifact (ABI + bytecode + constructor-args spec); the
// browser signs and sends the deploy transaction with the admin's wallet.

import React, { useState } from 'react';
import { useAccount, useChainId, usePublicClient, useWalletClient, useSwitchChain } from 'wagmi';
import { useUnifiedWallet } from '../contexts/UnifiedWalletContext';
import { chainConfig, BSC_CHAIN_ID, addressUrl, txUrl } from '../config/chains';
import { useActiveContracts } from '../hooks/useActiveContracts';
import api from '../services/api';
import './DeployContract.css';

const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'Floobiz';
const DEFAULT_TOKEN_NAME = process.env.REACT_APP_DEFAULT_TOKEN_NAME || SITE_NAME;
const DEFAULT_TOKEN_SYMBOL = process.env.REACT_APP_DEFAULT_TOKEN_SYMBOL || 'FBIZ';

// Hardcoded in BSCToken.sol; shown here read-only for the admin.
const FIXED_SUPPLY = '1,000,000,000';

function DeployContract() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain } = useSwitchChain();
  const { connectWallet } = useUnifiedWallet();
  const { tokenAddress: existingTokenAddress, loading: contractsLoading, refetch: refetchContracts } = useActiveContracts();

  const [form, setForm] = useState({
    name: DEFAULT_TOKEN_NAME,
    symbol: DEFAULT_TOKEN_SYMBOL,
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const wrongChain = isConnected && chainId !== BSC_CHAIN_ID;
  const bsc = chainConfig('bsc');
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleDeploy(e) {
    e.preventDefault();
    if (!isConnected) { await connectWallet(); return; }
    if (wrongChain) { await switchChain({ chainId: BSC_CHAIN_ID }); return; }
    if (!walletClient || !publicClient) return;

    setBusy(true);
    setStatus({ phase: 'preparing', message: 'Fetching contract artifact...' });

    try {
      const resp = await api.post('/contracts/deploy', {
        kind: 'token',
        name: form.name,
        symbol: form.symbol,
      });
      const { abi, bytecode, constructorArgs } = resp.data.data;

      setStatus({ phase: 'signing', message: 'Confirm the deploy in MetaMask...' });
      const hash = await walletClient.deployContract({
        abi,
        bytecode,
        args: constructorArgs,
      });
      setStatus({ phase: 'mining', message: 'Deploy submitted. Waiting for confirmation...', txHash: hash });

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      const contractAddress = receipt.contractAddress;

      await api.post('/contracts/save-deployed', {
        contractAddress,
        transactionHash: hash,
        deployerAddress: address,
        tokenName: form.name,
        tokenSymbol: form.symbol,
        totalSupply: '1000000000',
        blockNumber: Number(receipt.blockNumber),
        contractType: 'bsc_token',
      });

      setStatus({
        phase: 'done',
        message: `Deployed! Contract: ${contractAddress}`,
        txHash: hash,
        contractAddress,
      });
    } catch (err) {
      console.error(err);
      const apiMsg = err?.response?.data?.message;
      if (err?.response?.data?.code === 'ALREADY_DEPLOYED') {
        refetchContracts();
      }
      setStatus({ phase: 'error', message: apiMsg || err?.shortMessage || err?.message || 'Deploy failed' });
    } finally {
      setBusy(false);
    }
  }

  if (contractsLoading) {
    return (
      <div className="deploy-container">
        <h2>Deploy {SITE_NAME} Token</h2>
        <p className="deploy-sub">Loading…</p>
      </div>
    );
  }

  if (existingTokenAddress) {
    return (
      <div className="deploy-container">
        <h2>Deploy {SITE_NAME} Token</h2>
        <div className="deploy-warning">
          A token is already deployed for this network. Only one token contract is allowed.
          <div style={{ marginTop: 8 }}>
            Token:{' '}
            <a href={addressUrl('bsc', existingTokenAddress)} target="_blank" rel="noreferrer">
              {existingTokenAddress}
            </a>
          </div>
          <div style={{ marginTop: 8 }}>
            Next: configure sale rate / allocation / treasury in{' '}
            <a href="/admin/presale">Presale Settings</a>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="deploy-container">
      <h2>Deploy {SITE_NAME} Token</h2>
      <p className="deploy-sub">Target network: <strong>{bsc.name}</strong>. You sign and pay gas with your own wallet.</p>

      {!isConnected && (
        <button className="deploy-btn" onClick={connectWallet}>Connect wallet to continue</button>
      )}

      {isConnected && wrongChain && (
        <div className="deploy-warning">
          You're on chain {chainId}.{' '}
          <button onClick={() => switchChain({ chainId: BSC_CHAIN_ID })}>Switch to {bsc.name}</button>
        </div>
      )}

      <form onSubmit={handleDeploy} className="deploy-form">
        <label>
          Token name
          <input value={form.name} onChange={onChange('name')} required disabled={busy} />
        </label>
        <label>
          Symbol
          <input value={form.symbol} onChange={onChange('symbol')} required disabled={busy} maxLength={11} />
        </label>
        <div className="deploy-fixed-supply">
          <div className="deploy-fixed-supply-label">Total supply</div>
          <div className="deploy-fixed-supply-value">{FIXED_SUPPLY} <span>tokens</span></div>
          <div className="deploy-fixed-supply-note">
            Fixed at deployment. No mint, no burn — supply can never change.
          </div>
        </div>
        <button type="submit" className="deploy-btn" disabled={busy || !isConnected || wrongChain}>
          {busy ? 'Working...' : `Deploy ${form.symbol || 'token'}`}
        </button>
      </form>

      {status && (
        <div className={`deploy-status deploy-status-${status.phase}`}>
          <div>{status.message}</div>
          {status.txHash && (
            <div>
              Tx:{' '}
              <a href={txUrl('bsc', status.txHash)} target="_blank" rel="noreferrer">
                {status.txHash.slice(0, 10)}…
              </a>
            </div>
          )}
          {status.contractAddress && (
            <div>
              Contract:{' '}
              <a href={addressUrl('bsc', status.contractAddress)} target="_blank" rel="noreferrer">
                {status.contractAddress}
              </a>
              <div className="deploy-next">
                Next: fund the Sale contract with your sale allocation, then configure rate &amp;
                treasury in <a href="/admin/presale">Presale Settings</a>.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DeployContract;
